import { useEffect, useRef } from "react";

/**
 * SSE subscriber that *appends* to local state instead of triggering a refetch.
 *
 * Used by the per-device activity feed: every `device:activity` event for the
 * current page's deviceId gets prepended to the list as it arrives, so the UI
 * is live without needing to re-query the API.
 *
 * The handler ref pattern lets the component pass an inline callback that
 * captures fresh state without forcing the EventSource to reconnect on each
 * render.
 */
export function useSSEAppend<T = unknown>(
  url: string,
  eventName: string,
  onEvent: (payload: T) => void,
  enabled: boolean = true
): void {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!url || !enabled) return;

    const es = new EventSource(url, { withCredentials: true });

    const handler = (ev: MessageEvent) => {
      try {
        const data = JSON.parse(ev.data) as T;
        handlerRef.current(data);
      } catch {
        // Malformed payload — ignore. Server-side validation should prevent this.
      }
    };

    es.addEventListener(eventName, handler as EventListener);

    es.onerror = () => {
      // EventSource auto-reconnects after a brief delay.
    };

    return () => {
      es.removeEventListener(eventName, handler as EventListener);
      es.close();
    };
  }, [url, eventName, enabled]);
}
